import { useEffect, useState, useRef } from 'react'
import useWindowSize from '../../hooks/useWindowSize'
import { getQueryParams } from '../../utils/location'

export default function Image (props = {
    imgRef: null,
    src: null,
    width: null,
    height: null,
    loading: null,
    textFallback: null,
    imgFallback: null
}) {
    const {
        src,
        width,
        height,
        loading,
        imgRef,
        textFallback,
        imgFallback,
        ...renderProps
    } = props

    let img = {}

    try {
        img = useRef()
    }
    catch (e) {
        console.warn(e)
    }

    const onRefCallback = (ref) => {
        img.current = ref

        if (imgRef
                && typeof imgRef === 'function')
            imgRef(ref)
    }

    const placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAARzQklUCAgICHwIZIgAAAALSURBVAhbY2AAAgAABQABHh/sFAAAAABJRU5ErkJggg=='

    const isLazyLoad = loading
        && loading === 'lazy'

    const [inViewport, setInViewport] = useState(!isLazyLoad)

    const [state, setState] = useState({
        error: !src || null,
        src: placeholder,
        width,
        height
    })

    useEffect(() => {
        if (!inViewport
                || !img.current
                || state.src !== placeholder
                || state.error)
            return

        const isUpload = Boolean((src || '')
            .match(/^\/storage\/o\//))

        const queryObj = isUpload
            && getQueryParams(src)

        if (!isUpload
                || !Object.keys(queryObj).length) {
            try {
                setState({ src, width, height })
            }
            catch (err) {}

            return
        }

        const image = new window.Image()

        const getSrc = () => isUpload
            ? src.replace(/(\.svg)\?.+$/, '$1')
            : src

        image.onerror = (e) => setState({
            error: true,
            src: imgFallback || placeholder,
            width,
            height
        })

        image.onload = (e) => setState({
            error: false,
            src: getSrc(),
            width: image.width,
            height: image.height
        })

        image.src = getSrc()
    }, [state, inViewport])

    useEffect(() => {
        if (!src
                || state.src === src
                || (state.src || '').replace(/(\?.+$)/, '')
                    === (props.src || '').replace(/\?.+$/, ''))
            return

        setState({
            error: null,
            src: placeholder,
            width,
            height
        })
    }, [])

    const size = useWindowSize()

    useEffect(() => {
        if (!img.current
                || inViewport)
            return

        const onInViewportDetection = () => {
            if (!img.current)
                return

            const bounds = img.current.getBoundingClientRect()
            const bottom = bounds.top + bounds.bottom

            const isVisibleTop = bounds.top >= 0
                && bounds.top < window.innerHeight

            const isVisibleBottom = bottom >= 0
                && bottom < window.innerHeight

            if (!(isVisibleTop || isVisibleBottom))
                return

            setInViewport(true)

            window.removeEventListener('scroll', onInViewportDetection)
        }

        onInViewportDetection()

        window.addEventListener('scroll', onInViewportDetection)

        return () => window.removeEventListener('scroll', onInViewportDetection)
    }, [img.current, size])

    if (isLazyLoad)
        renderProps.style = Object.assign(
            JSON.parse(JSON.stringify(renderProps.style || {})), {
                opacity: inViewport
                        && state.src !== placeholder
                    ? 1
                    : 0,
                transition: `opacity 500ms${
                    (renderProps.style || {}).transition
                        ? ', '
                        : ''}${
                    (renderProps.style || {}).transition || ''}`
            })

    if (state.error
            && textFallback)
        return <span>{textFallback}</span>

    return !state.error
            || (state.src
                    && state.src !== placeholder)
        ? (
            <img
                ref={onRefCallback}
                alt=""
                src={state.src}
                width={state.width}
                height={state.height}
                {...renderProps}
            />)
        : null
}
